import apiClient from "@/services/api/client";
import urlBuilder from "@/services/api/urlBuilder";

export interface User {
  id: string;
  email: string;
  /** Legacy single name — retained for backward compat; first_name/last_name are the source of truth. */
  name?: string;
  first_name?: string;
  last_name?: string;
  role?: string;
  /** StoreManager users only — the stores they're linked to. */
  stores?: string[];
  [key: string]: unknown;
}

export interface GetUsersParams {
  page?: number;
  currentPage?: number;
  limit?: number;
  /** Admin: filter by merchant */
  merchantId?: string;
  email?: string;
  role?: string;
}

export interface UsersResponse {
  docs: User[];
  page: number;
  totalPages: number;
  totalDocs: number;
}

/**
 * Get all users
 */
export const getUsers = async (params?: GetUsersParams): Promise<UsersResponse> => {
  const response = await apiClient.post(urlBuilder.buildUrl("user-getUsers"), {
    currentPage: params?.currentPage ?? params?.page ?? 1,
    page: params?.page ?? params?.currentPage ?? 1,
    limit: params?.limit ?? 10,
    ...(params?.merchantId ? { merchantId: params.merchantId } : {}),
    ...(params?.email ? { email: params.email } : {}),
    ...(params?.role ? { role: params.role } : {}),
  });

  return response.data?.data || response.data;
};

/**
 * Get a single user by ID
 */
export const getUser = async (userId: string): Promise<User> => {
  const response = await apiClient.post(urlBuilder.buildUrl("user-getUser"), { userId });
  return response.data?.data || response.data;
};

/**
 * Create a new user
 */
export const createUser = async (userData: Partial<User>): Promise<User> => {
  const response = await apiClient.post(urlBuilder.buildUrl("user-createUser"), userData);
  return response.data?.data || response.data;
};

/**
 * Update a user
 */
export const updateUser = async (userId: string, userData: Partial<User>): Promise<User> => {
  const response = await apiClient.post(urlBuilder.buildUrl("user-updateUser"), {
    userId,
    ...userData,
  });
  return response.data?.data || response.data;
};

export interface ImpersonateUserResult {
  token: string;
  userData: Record<string, unknown>;
}

export const impersonateUser = async (userId: string): Promise<ImpersonateUserResult> => {
  const response = await apiClient.post(urlBuilder.buildUrl("user-impersonate"), { userId });
  const layer = response.data?.data ?? response.data;
  const token = typeof layer?.token === "string" ? layer.token : "";
  const userData =
    layer?.userData && typeof layer.userData === "object"
      ? (layer.userData as Record<string, unknown>)
      : null;
  if (!token || !userData) {
    throw new Error("Impersonation response missing token or user data");
  }
  return { token, userData };
};
